import { Theme, createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid-pro/themeAugmentation";
import colorPalette from "./colors/dark-theme";
import { MuiAccordion, MuiAccordionDetails, MuiAccordionSummary } from "./MuiAccordion/MuiAccordion";
import MUIAlert from "./MUIAlert/MUIAlert";
import MUIButton from "./MUIButton/MUIButton";
import MUIChip from "./MUIChip/MUIChip";
import MuiDataGrid from "./MuiDataGrid/MuiDataGrid";
import MuiSlider from "./MUISlider";
import MUIToggleButton from "./MUIToggleButton/MUIToggleButton";
export interface ICustom {
  AppLaunchpad: {
    width?: number;
    backgroundColor: string;
  };
  launchpad: {
    iconColor: string;
    iconHoverColor: string;
    iconActiveColor: string;
  };
  appBar: {
    height?: number;
    backgroundColor: string;
  };
  statusBar: {
    height?: number;
    foregroundColor: string;
    backgroundColor: string;
  };
  mosaicBar: {
    height?: number;
  };
  timeline: {
    groupColor: string;
    groupColumnDivider: string;
    groupSelected: string;
    contextListBackground: string;
    aircraftRegColor: string;
    aircraftFleetColor: string;
    aircraftTypeColor: string;
    aircraftSelected: string;
    webglCanvas: {
      background: string;
      clock: string;
      groupLabel1: string;
      groupLabel2: string;
      selectedRow: string;
      evenRow: string;
      oddRow: string;
      intervalBar: string;
      timeScale1: string;
      timeScale2: string;
      scrollBarBackground: string;
      scrollBarThumb: {
        base: string;
        hover: string;
        active: string;
      };
      biddingActiveButtonColor: string;
    };
  };
  tables: {
    backgroundColor: string;
    contentColor: string;
    evenRowColor: string;
    oddRowColor: string;
    evenRowHoverColor: string;
    oddRowHoverColor: string;
  };
  forms: {
    labels: string;
    inputText: string;
    inputBack: string;
    inputBorder: string;
    pristineColor: string;
    focusedColor: string;
    dirtyColor: string;
    alternateDirtyColor: string;
    errorColor: string;
    warningColor: string;
  };
  modal: {
    title: string;
    message: string;
    error: string;
    warning: string;
  };
  comments: {
    card: {
      contentBackGround: string;
      headerText: string;
      contrastText: string;
      avatar: string;
    };
  };
  treeview: {
    itemColor: string;
    itemHoverColor: string;
    itemActiveColor: string;
    itemActiveBackground: string;
  };
  pairingDisplay: {
    backgroundColor: string;
    pairingNotes: {
      backgroundColor: string;
    };
  };
  contextPopover: {
    foreGroundColor: string;
    backgroundColor: string;
  };
  history: {
    modfiedField: string;
    addedField: string;
  };
  bidding: {
    activeBackgroundColor: string;
    blockDayBackgroundColor: string;
    blockWeekendBackgroundColor: string;
  };
  pairingStatus: {
    noPairingColor: string;
    missingCrewColor: string;
    extraCrewColor: string;
    extraPosColor: string;
    missingACInfoColor: string;
    okColor: string;
  };
}
declare module "@mui/material/styles" {
  interface Theme {
    custom: ICustom;
  }
  interface ThemeOptions {
    custom: ICustom;
  }
  interface Palette {
    dev?: Palette["primary"];
  }

  interface PaletteOptions {
    dev?: PaletteOptions["primary"];
  }
}
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    dev: true;
  }
}
declare module "@mui/material/Alert" {
  interface AlertPropsColorOverrides {
    dev: true;
  }
}

const darkTheme: Theme = createTheme({
  breakpoints: {
    keys: ["xs", "sm", "md", "lg", "xl"],
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  direction: "ltr",
  mixins: {
    toolbar: {
      minHeight: 56,
      "@media (min-width:0px) and (orientation: landscape)": {
        minHeight: 48,
      },
      "@media (min-width:600px)": {
        minHeight: 64,
      },
    },
  },
  components: {
    MuiDataGrid: {
      ...MuiDataGrid,
    },
    MuiButton: { ...MUIButton },
    MuiChip: { ...MUIChip },
    MuiToggleButton: { ...MUIToggleButton },
    MuiAlert: { ...MUIAlert },
    MuiAccordion: { ...MuiAccordion },
    MuiAccordionSummary: { ...MuiAccordionSummary },
    MuiAccordionDetails: { ...MuiAccordionDetails },
    MuiSlider: { ...MuiSlider },
    MuiGrid: {
      defaultProps: {
        zeroMinWidth: true,
      },
    },
  },
  palette: {
    ...colorPalette,
  },
  shadows: [
    "none",
    "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
    "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
    "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
    "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
    "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
    "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)",
    "0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
    "0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
    "0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
    "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
    "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
    "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
    "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
    "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
    "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
    "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
    "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
    "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
    "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
    "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)",
  ],
  typography: {
    htmlFontSize: 16,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontSize: "6rem",
      lineHeight: 1.167,
      letterSpacing: "-0.01562em",
    },
    h2: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontSize: "3.75rem",
      lineHeight: 1.2,
      letterSpacing: "-0.00833em",
    },
    h3: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: "3rem",
      lineHeight: 1.167,
      letterSpacing: "0em",
    },
    h4: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: "2.125rem",
      lineHeight: 1.235,
      letterSpacing: "0.00735em",
    },
    h5: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: "1.5rem",
      lineHeight: 1.334,
      letterSpacing: "0em",
    },
    h6: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      fontSize: "1.25rem",
      lineHeight: 1.6,
      letterSpacing: "0.0075em",
    },
    subtitle1: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontSize: 14,
      lineHeight: 1.75,
      letterSpacing: "0.00938em",
      color: "#8dabc4",
    },
    subtitle2: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.57,
      letterSpacing: "0.00714em",
    },
    body1: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: 1.5,
      letterSpacing: "0.00938em",
      color: "#9fb1c1",
    },
    body2: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: 1.43,
      letterSpacing: "0.01071em",
    },
    button: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.75,
      letterSpacing: "0.02857em",
      textTransform: "uppercase",
    },
    caption: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 1.66,
      letterSpacing: "0.03333em",
    },
    overline: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 2.66,
      letterSpacing: "0.08333em",
      textTransform: "uppercase",
    },
  },
  shape: {
    borderRadius: 4,
  },
  transitions: {
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 500,
      leavingScreen: 200,
    },
  },
  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  custom: {
    AppLaunchpad: {
      width: 80,
      backgroundColor: "#1a2738",
    },
    launchpad: {
      iconColor: "#8dabc4",
      iconHoverColor: "#ffffff",
      iconActiveColor: "#ffffff",
    },
    appBar: {
      height: 48,
      backgroundColor: "#005176",
    },
    statusBar: {
      height: 36,
      foregroundColor: "#ffffff",
      backgroundColor: "#005176",
    },
    mosaicBar: {
      height: 36,
    },
    timeline: {
      groupColor: "#9fb1c1",
      groupColumnDivider: "#9fb1c1",
      groupSelected: "#ffb74d",
      contextListBackground: "#2d3e51",
      aircraftRegColor: "#ffffff",
      aircraftFleetColor: "#aaaaaa",
      aircraftTypeColor: "#67859e",
      aircraftSelected: "#ffffff",
      webglCanvas: {
        background: "#212F41",
        clock: "#FF9800",
        groupLabel1: "#ffffff",
        groupLabel2: "#2d3e51",
        // Rows in the gantt are painted one pixel shorter than the row pitch, so the canvas clear colour
        // (#1a1a1a) shows through as the line between them. A selected row has to differ from that line or
        // the rows run together into one block - which #191919 did. This navy also carries the same blue as
        // the selected aircraft chip, so a selected row reads as one band across the column and the gantt.
        selectedRow: "#0e3247",
        evenRow: "#000000",
        oddRow: "#000000",
        intervalBar: "#FF9800",
        timeScale1: "#ffffff",
        timeScale2: "#5d6e81",
        scrollBarBackground: "#212F41",
        scrollBarThumb: {
          base: "#9fb1c1",
          hover: "#486c8e",
          active: "#345b7f",
        },
        biddingActiveButtonColor: "#008080",
      },
    },
    tables: {
      backgroundColor: "#2d3e51",
      contentColor: "#a4bed6",
      evenRowColor: "#1A2738",
      oddRowColor: "#212F41",
      evenRowHoverColor: "#191919",
      oddRowHoverColor: "#191919",
    },
    forms: {
      labels: "#9fb1c1",
      inputText: "#a4bed6",
      inputBack: "#1A2738",
      inputBorder: "#334b69",
      pristineColor: "#9fb1c1",
      focusedColor: "#9fb1c1",
      dirtyColor: "#87Ed8C",
      alternateDirtyColor: "#3bcc3f",
      errorColor: "#FF2C55",
      warningColor: "#FF9800",
    },
    modal: {
      title: "#ffffff",
      message: "#9fb1c1",
      error: "#FF2C55",
      warning: "#FF9800",
    },
    comments: {
      card: {
        contentBackGround: "#263647",
        headerText: "#8dabc4",
        contrastText: "#ffffff",
        avatar: "#ffffff",
      },
    },
    treeview: {
      itemColor: "#8dabc4",
      itemHoverColor: "#ffffff",
      itemActiveColor: "#ffffff",
      itemActiveBackground: "#0087C6",
    },
    pairingDisplay: {
      backgroundColor: "#101010",
      pairingNotes: {
        backgroundColor: "#FFD486",
      },
    },
    contextPopover: {
      foreGroundColor: "#9fb1c1",
      backgroundColor: "#000000",
    },
    history: {
      modfiedField: "#ffa500",
      addedField: "#00aa52",
    },
    bidding: {
      activeBackgroundColor: "#075151",
      blockDayBackgroundColor: "#011414",
      blockWeekendBackgroundColor: "#032626",
    },
    pairingStatus: {
      noPairingColor: "#ff3200",
      missingCrewColor: "#f9dc00",
      extraCrewColor: "#00b6ff",
      extraPosColor: "#cccccc",
      missingACInfoColor: "#ffa526",
      okColor: "#00e50f",
    },
  },
});

export default darkTheme;
